/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { NetworkEnum, RoutingEnum } from '../enums/api';
import { NetworkInterfaceModel, RouteModel, RoutingModel } from '../models';
import { Observable } from 'rxjs';
import { ErrorResponseModel } from '../../../../core/models';

/**
 * Network Service
 * Note: This class is to be used to make API calls
 *       and return with a structured response.
 */
@Injectable()
export class RoutingApiService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Add route
     * @param route
     */
    public addRoute(network, gateway, options?: any): Observable<any> {
        const route: RouteModel = {
            id: '',
            formNetworkName: '',
            formGatewayName: '',
            network: network,
            gateway: gateway
        };

        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(
                    RoutingEnum.POST_ROUTE_ADD,
                    route,
                    moduleAuth,
                    true
                ).subscribe(
                    (response) => {
                        observer.next(response);
                        observer.complete();
                    },
                    error => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Get routes
     */
    public getRoutes(options?: any): Observable<RoutingModel> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.get(
                RoutingEnum.GET_ALL_ROUTES,
                moduleAuth,
                true
            ).subscribe(
                data => {
                    const setting = <RoutingModel>{};
                    setting.ipv4gateway = data['ipv4gateway'];
                    setting.ipv6gateway = data['ipv6gateway'];
                    setting.ipv6interface = data['ipv6interface'];
                    setting.ipv4interface = data['ipv4interface'];
                    setting.staticroutes = data['staticroutes'];
                    observer.next(setting);
                    observer.complete();
                },
                error => {
                    observer.next(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Delete route
     */
    public deleteRoute(routeID: string, options?: any): Observable<object> {
        return new Observable<object>((observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(
                    RoutingEnum.POST_ROUTE_DEL(routeID),
                    moduleAuth,
                    true
                ).subscribe(
                    (response: object) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: any) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Delete gateway
     * @param ipVersion
     * @param options
     */
    public deleteGateway(ipVersion: string | number, options?: any): Observable<object> {
        return new Observable<object>((observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(
                    RoutingEnum.DELETE_DEFAULTGW(ipVersion.toString()),
                    moduleAuth,
                    true
                ).subscribe(
                    (response: object): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Set default gateway
     * @param gateway
     * @param options
     */
    public setDefaultGateway(gateway: { gateway: any; interface: any; }, options?: any): Observable<object> {
        return new Observable((observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(
                    RoutingEnum.PUT_DEFAULTGW_SET,
                    gateway,
                    moduleAuth,
                    true
                ).subscribe(
                    (response: object): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Get network interfaces.
     */
    public getInterfaces(options?: any): Observable<NetworkInterfaceModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.get(
                NetworkEnum.GET_INTERFACE_ALL,
                moduleAuth,
                true
            ).subscribe(
                (data) => {
                    const interfaces: NetworkInterfaceModel[] = data;

                    const autoInterface = <NetworkInterfaceModel>{};
                    autoInterface.name = 'auto';
                    interfaces.unshift(autoInterface);

                    observer.next(interfaces);
                    observer.complete();
                },
                error => {
                    observer.next(error);
                    observer.complete();
                }
            );
        });
    }
}
